'use client';
import React, { useState, useRef } from 'react';
import { HelpCircle, Send, Paperclip, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface SupportTicketFormProps {
  userId: string;
  orders: { id: string; orderNumber: string }[];
}

const ISSUE_TYPES = [
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'artwork', label: 'Artwork & Design' },
  { value: 'delivery', label: 'Campaign Delivery' },
  { value: 'order', label: 'Order Issue' },
  { value: 'technical', label: 'Technical Problem' },
  { value: 'general', label: 'General Inquiry' },
];

export default function SupportTicketForm({ userId, orders }: SupportTicketFormProps) {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > 10 * 1024 * 1024) {
      setErrorMsg('File must be under 10 MB.');
      return;
    }
    setAttachment(file);
    setErrorMsg('');
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueType || !description.trim()) {
      setErrorMsg('Please select an issue type and provide a description.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const supabase = createClient();
      let attachmentUrl: string | null = null;

      // Upload attachment if present
      if (attachment) {
        const ext = attachment.name.split('.').pop();
        const filePath = `support/${userId}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('support-attachments')
          .upload(filePath, attachment, { upsert: false });

        if (uploadError) {
          // If bucket doesn't exist, skip attachment silently
          console.warn('Attachment upload skipped:', uploadError.message);
        } else {
          const { data: urlData } = supabase.storage
            .from('support-attachments')
            .getPublicUrl(filePath);
          attachmentUrl = urlData?.publicUrl ?? null;
        }
      }

      const issueLabel = ISSUE_TYPES.find((t) => t.value === issueType)?.label ?? issueType;

      const { error: insertError } = await supabase.from('support_messages').insert({
        user_id: userId,
        order_id: selectedOrderId || null,
        subject: issueLabel,
        category: issueType,
        message: description.trim(),
        sender_role: 'customer',
        attachment_url: attachmentUrl,
        root_user_id: userId,
      });

      if (insertError) throw new Error(insertError.message);

      setSubmitStatus('success');
      setIssueType('');
      setDescription('');
      setSelectedOrderId('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (err: any) {
      setErrorMsg(err.message ?? 'Failed to submit ticket. Please try again.');
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl border border-white/8 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-white/8 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-brand-600/15 border border-brand-600/20 flex items-center justify-center shrink-0">
          <HelpCircle size={17} className="text-brand-400" />
        </div>
        <div>
          <h2 className="font-semibold text-white text-sm">Submit a Support Ticket</h2>
          <p className="text-xs text-slate-400 mt-0.5">Our team typically responds within 1 business day</p>
        </div>
      </div>

      {/* Success state */}
      {submitStatus === 'success' && (
        <div className="mx-6 mt-5 flex items-start gap-3 p-4 rounded-xl bg-green-500/8 border border-green-500/20">
          <CheckCircle size={16} className="text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-green-300">Ticket submitted successfully!</p>
            <p className="text-xs text-slate-400 mt-0.5">We'll review your request and get back to you shortly.</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
        {/* Issue type + Order (side by side on md+) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Issue type */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Issue Type <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                required
                className="w-full appearance-none bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-brand-500/50 focus:bg-white/8 transition-all pr-9"
              >
                <option value="" disabled className="bg-navy-900 text-slate-400">Select issue type…</option>
                {ISSUE_TYPES.map((t) => (
                  <option key={t.value} value={t.value} className="bg-navy-900 text-white">{t.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* Related order (optional) */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Related Order <span className="text-slate-600">(optional)</span>
            </label>
            <div className="relative">
              <select
                value={selectedOrderId}
                onChange={(e) => setSelectedOrderId(e.target.value)}
                className="w-full appearance-none bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-brand-500/50 focus:bg-white/8 transition-all pr-9"
              >
                <option value="" className="bg-navy-900 text-slate-400">No specific order</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id} className="bg-navy-900 text-white">{o.orderNumber}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            placeholder="Describe your issue in detail…"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500/50 focus:bg-white/8 transition-all resize-none"
          />
          <p className="text-[11px] text-slate-600 mt-1 text-right">{description.length}/2000</p>
        </div>

        {/* Attachment */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Attachment <span className="text-slate-600">(optional, max 10 MB)</span>
          </label>
          {attachment ? (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl">
              <Paperclip size={14} className="text-brand-400 shrink-0" />
              <span className="text-sm text-slate-300 truncate flex-1">{attachment.name}</span>
              <span className="text-xs text-slate-500 shrink-0">{(attachment.size / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-slate-500 hover:text-red-400 transition-colors shrink-0"
                aria-label="Remove attachment"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-dashed border-white/15 rounded-xl text-sm text-slate-400 hover:text-white hover:border-white/25 hover:bg-white/8 transition-all w-full justify-center"
            >
              <Paperclip size={14} />
              Click to attach a file
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-500/8 border border-red-500/20">
            <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
            <p className="text-xs text-red-300">{errorMsg}</p>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={submitting || !issueType || !description.trim()}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <Send size={14} />
                Submit Ticket
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
